import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Check,
  Star,
  Smartphone,
  QrCode,
  TrendingUp,
  ShieldCheck,
  Zap,
  Mail,
} from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";

const CHEVALET_IMG =
  "https://customer-assets.emergentagent.com/job_google-chevalet/artifacts/is4v9l7e_1000202828.jpg";

const CANONICAL = "https://gmpdeveloppement.fr/products/votre-chevalet-google";

const BENEFITS = [
  { icon: TrendingUp, t: "Jusqu'à 2× plus d'avis Google", d: "Vous collectez les avis au moment exact où le client est satisfait : à la caisse, au comptoir, en fin de service." },
  { icon: Smartphone, t: "Un simple tap NFC, zéro application", d: "Le client approche son smartphone ou scanne le QR code : votre fiche Google s'ouvre instantanément." },
  { icon: TrendingUp, t: "Meilleur classement local", d: "Plus d'avis 5 étoiles = une fiche Google qui grimpe et qui passe devant vos concurrents dans le secteur." },
  { icon: ShieldCheck, t: "Compatible tous smartphones", d: "NFC pour les téléphones récents, QR code pour tous les autres. Aucun client laissé de côté." },
  { icon: Zap, t: "Posé, ça fonctionne", d: "Aucune installation, aucune configuration. Vous le posez sur le comptoir et la collecte démarre." },
  { icon: QrCode, t: "QR modifiable à distance", d: "Avec l'espace en ligne, changez la destination de votre QR sans jamais réimprimer le chevalet." },
];

const STEPS = [
  { t: "Le client termine son achat", d: "Au moment où il est le plus satisfait de votre accueil et de votre service." },
  { t: "Il approche son téléphone ou scanne", d: "Tap NFC sans contact ou QR code : votre page d'avis Google s'ouvre en une seconde." },
  { t: "Il laisse un avis 5 étoiles", d: "Votre note moyenne progresse et votre fiche grimpe dans le classement local." },
];

const USECASES = [
  "Restaurants & bars",
  "Commerces de proximité",
  "Agences & assurances",
  "Coiffure & beauté",
  "Garages & automobile",
  "Hôtels & gîtes",
  "Cabinets & santé",
  "Réseaux multi-sites",
];

const FAQ = [
  {
    q: "Qu'est-ce qu'un chevalet avis Google ?",
    a: "Un chevalet avis Google est un support physique connecté (NFC + QR code) que vous posez en caisse ou sur le comptoir. Au moment où votre client est satisfait, il approche son smartphone ou scanne le QR code et arrive directement sur votre page d'avis Google. C'est la méthode la plus simple et la plus rapide pour collecter des avis 5 étoiles.",
  },
  {
    q: "Comment le chevalet augmente-t-il mes avis Google ?",
    a: "Le chevalet capte le client au bon moment, sans friction : pas d'application à installer, pas d'email à taper. Un simple geste suffit. Résultat : nos clients constatent généralement jusqu'à deux fois plus d'avis collectés en quelques semaines.",
  },
  {
    q: "La technologie NFC est-elle compatible avec tous les téléphones ?",
    a: "Le NFC fonctionne avec la grande majorité des smartphones récents (iPhone et Android). Pour les téléphones plus anciens, le QR code imprimé sur le chevalet prend le relais : absolument personne n'est laissé de côté.",
  },
  {
    q: "Dois-je installer une application ou un logiciel ?",
    a: "Non. Le chevalet fonctionne dès qu'il est posé. Côté client, aucune application n'est nécessaire. Vous pouvez aussi activer notre espace en ligne pour suivre les scans, modifier la destination du QR et centraliser vos avis.",
  },
  {
    q: "Combien coûte un chevalet avis Google ?",
    a: "Le chevalet avis Google est proposé à partir de 39 € HT l'unité, personnalisé à votre charte graphique. Pour les réseaux multi-sites, contactez-nous pour un tarif sur mesure.",
  },
  {
    q: "Puis-je personnaliser mon chevalet ?",
    a: "Oui. Chaque chevalet est personnalisé à vos couleurs, votre logo et votre message. Nous concevons et produisons tout en interne, du design à la fabrication.",
  },
];

const StarRow = () => (
  <span className="inline-flex gap-0.5" aria-hidden="true">
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} size={16} className="text-warm fill-warm" />
    ))}
  </span>
);

export default function ChevaletGoogle() {
  const contactHref = `/contact?sujet=${encodeURIComponent("Chevalet Avis Google")}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Chevalet Avis Google — Présentoir NFC & QR code",
    image: [CHEVALET_IMG],
    description:
      "Chevalet avis Google connecté en NFC et QR code. Le présentoir qui transforme vos clients satisfaits en avis 5 étoiles et booste votre fiche Google Business.",
    brand: { "@type": "Brand", name: "GMP Développement" },
    category: "Présentoir avis Google connecté",
    offers: {
      "@type": "Offer",
      priceCurrency: "EUR",
      price: "39.00",
      priceValidUntil: "2026-12-31",
      availability: "https://schema.org/InStock",
      url: CANONICAL,
      seller: { "@type": "Organization", name: "GMP Développement" },
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "127",
    },
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: "https://gmpdeveloppement.fr/" },
      { "@type": "ListItem", position: 2, name: "Chevalet Avis Google", item: CANONICAL },
    ],
  };

  return (
    <div className="grain relative min-h-screen" data-testid="chevalet-google-page">
      <Helmet>
        <title>Chevalet Avis Google NFC & QR Code — 2× plus d'avis 5 étoiles | GMP Développement</title>
        <meta
          name="description"
          content="Le chevalet avis Google connecté (NFC + QR code) qui transforme vos clients satisfaits en avis 5 étoiles. Boostez votre fiche Google Business et dépassez vos concurrents. À partir de 39 € HT, personnalisé."
        />
        <meta
          name="keywords"
          content="chevalet avis google, chevalet nfc avis google, présentoir avis google, plaque avis google qr code, borne avis google, augmenter ses avis google, chevalet google reviews, support avis google, qr code avis google"
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content="Chevalet Avis Google NFC & QR Code — 2× plus d'avis 5 étoiles" />
        <meta
          property="og:description"
          content="Le présentoir connecté qui transforme vos clients satisfaits en avis 5 étoiles. NFC + QR, personnalisé, à partir de 39 € HT."
        />
        <meta property="og:image" content={CHEVALET_IMG} />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:locale" content="fr_FR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Chevalet Avis Google NFC & QR Code — 2× plus d'avis 5 étoiles" />
        <meta name="twitter:description" content="Le présentoir connecté qui collecte vos avis Google. NFC + QR, à partir de 39 € HT." />
        <meta name="twitter:image" content={CHEVALET_IMG} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </Helmet>

      <Navbar />

      {/* HERO — chevalet en fond */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="chevalet-hero">
        <div className="absolute inset-0">
          <img
            src={CHEVALET_IMG}
            alt="Chevalet avis Google connecté NFC et QR code posé sur un comptoir"
            className="w-full h-full object-cover"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/92 to-background/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full pt-28 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <StarRow />
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-warm">
                4,9/5 · NFC + QR code
              </span>
            </div>
            <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Chevalet <span className="text-accent">Avis Google</span>
            </h1>
            <p className="mt-3 font-heading font-semibold text-xl sm:text-2xl text-foreground/90">
              Le présentoir qui transforme vos clients en avis 5 étoiles.
            </p>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
              Un simple tap NFC ou un scan de QR code suffit. Vos clients satisfaits laissent un avis Google
              en une seconde, sans application. Votre fiche grimpe, vous dépassez vos concurrents locaux.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link
                to={contactHref}
                data-testid="chevalet-cta-primary"
                className="group bg-accent text-background font-semibold px-7 py-3.5 flex items-center justify-center gap-2 hover:bg-accent-hover transition-colors glow-accent"
              >
                Commander mon chevalet
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="#fonctionnement"
                className="border border-border text-foreground px-7 py-3.5 text-center hover:border-accent/50 transition-colors"
              >
                Voir comment ça marche
              </a>
            </div>
            <p className="mt-6 font-mono text-xs tracking-wider text-muted-foreground">
              À partir de <span className="text-warm font-semibold">39 € HT</span> · personnalisé à votre charte
            </p>
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-border bg-surface/50" data-testid="chevalet-stats">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            ["2×", "plus d'avis collectés"],
            ["1 s", "pour laisser un avis"],
            ["0", "application à installer"],
            ["100 %", "compatible smartphones"],
          ].map(([n, l]) => (
            <div key={l}>
              <p className="font-heading font-black text-3xl sm:text-4xl text-accent">{n}</p>
              <p className="text-sm text-muted-foreground mt-1">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 sm:py-24" data-testid="chevalet-benefits">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-accent mb-4">Pourquoi le chevalet avis Google</p>
          <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl max-w-2xl">
            La méthode la plus simple pour booster votre fiche Google.
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div
                  key={b.t}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
                  className="bg-surface border border-border p-7 hover:border-accent/40 transition-colors"
                >
                  <div className="w-11 h-11 flex items-center justify-center border border-accent/40 bg-accent/5 text-accent mb-5">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-heading font-semibold text-lg mb-2">{b.t}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{b.d}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="fonctionnement" className="py-20 sm:py-24 border-y border-border bg-surface/40 scroll-mt-24" data-testid="chevalet-how">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl mb-12">
            Comment fonctionne le chevalet
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.t} className="relative bg-background border border-border p-7">
                <span className="font-mono text-5xl font-bold text-white/5 absolute top-4 right-6">0{i + 1}</span>
                <h3 className="font-heading font-semibold text-lg mb-2 relative z-10">{s.t}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed relative z-10">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="py-20 sm:py-24" data-testid="chevalet-usecases">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-accent mb-4">Pour qui</p>
          <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl max-w-2xl mb-10">
            Pensé pour tous les commerces qui vivent de leur réputation locale.
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {USECASES.map((u) => (
              <div key={u} className="flex items-center gap-3 border border-border bg-surface px-5 py-4">
                <Check size={18} className="text-warm shrink-0" />
                <span className="text-sm text-foreground/90">{u}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — SEO */}
      <section className="py-20 sm:py-24 border-y border-border bg-surface/40" data-testid="chevalet-faq">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-accent mb-4">Questions fréquentes</p>
          <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl mb-10">
            Tout savoir sur le chevalet avis Google
          </h2>
          <div className="space-y-4">
            {FAQ.map((f) => (
              <details
                key={f.q}
                className="group bg-background border border-border p-6 [&_summary::-webkit-details-marker]:hidden"
                data-testid="chevalet-faq-item"
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <h3 className="font-heading font-semibold text-base sm:text-lg pr-6">{f.q}</h3>
                  <span className="text-accent shrink-0 transition-transform group-open:rotate-45 text-2xl leading-none">+</span>
                </summary>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 sm:py-24" data-testid="chevalet-final-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="relative border border-accent/30 bg-accent/5 p-8 sm:p-14 text-center overflow-hidden">
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[30rem] h-[30rem] bg-accent/10 blur-3xl rounded-full pointer-events-none" />
            <div className="relative z-10">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-3">
                Prêt à collecter 2× plus d'avis Google ?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
                Recevez votre chevalet personnalisé à votre charte graphique. Conception et production internes,
                accompagnement humain. Réponse sous 24 h.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to={contactHref}
                  data-testid="chevalet-cta-bottom"
                  className="inline-flex items-center justify-center gap-2 bg-accent text-background font-semibold px-7 py-3.5 hover:bg-accent-hover transition-colors glow-accent"
                >
                  <Mail size={18} /> Commander mon chevalet
                </Link>
                <Link
                  to="/#tarifs"
                  className="inline-flex items-center justify-center border border-border text-foreground px-7 py-3.5 hover:border-accent/50 transition-colors"
                >
                  Voir les tarifs
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
